import { createEvent, createEffect, restore, sample } from 'effector'
import { fx } from '../../libs'
import { CountriesListResponse } from './types'
import { env } from '../../libs/constants'
import { countries as localCountries } from '../../libs/countries'

const localCountryMap: Record<string, string> = {}
localCountries.forEach((c: { value: string; text: string }) => {
  localCountryMap[c.value] = c.text
})

const CACHE_KEY = 'crm:countries'
const CACHE_TTL = 10 * 60 * 1000 // 10 minutes

function getCachedCountries(): CountriesListResponse | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > CACHE_TTL) {
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

function setCachedCountries(data: CountriesListResponse) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }))
  } catch {
    // sessionStorage full or unavailable
  }
}

export const dictionariesMounted = createEvent()

export const getCountriesList = fx<void, CountriesListResponse | null>({
  method: 'GET',
  baseUrl: env.baseUrl && env.baseUrl.replace('v1', ''),
  url: 'county',
})

const fetchCountriesWithCache = createEffect(async () => {
  const cached = getCachedCountries()
  if (cached) return cached
  return null // will trigger API call
})

export const $countriesList = restore<CountriesListResponse | null>(
  getCountriesList.doneData.map((response) => response.body),
  getCachedCountries(),
)

// Save to cache on successful API response
$countriesList.watch((list) => {
  if (list) setCachedCountries(list)
})

export const $countriesByRegion = $countriesList.map(
  (list): Record<string, string[]> => {
    if (!list?.countries) return {}
    return Object.values(list.countries).reduce<Record<string, string[]>>(
      (acc, country) => {
        if (!country.region) return acc
        acc[country.region] ??= []
        acc[country.region].push(country.A3)
        return acc
      },
      {},
    )
  },
)

/** Code → Name mapping derived from $countriesList, with local fallback */
export const $countryNameByCode = $countriesList.map((list) => {
  if (list?.countries) {
    const map: Record<string, string> = { ...localCountryMap }
    Object.entries(list.countries).forEach(([code, country]) => {
      map[code] = country.name
    })
    return map
  }
  return localCountryMap
})

// On mount: check cache first, only call API if cache is empty/expired
sample({
  clock: dictionariesMounted,
  filter: () => getCachedCountries() === null,
  fn: () => ({}),
  target: getCountriesList,
})
